import React, { useState, useEffect, useRef } from 'react';
import { Container, Typography, TextField, Button, Box, Snackbar, Alert, InputLabel, MenuItem, Select, FormControl } from '@mui/material';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';

export default function CatchEntryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [species, setSpecies] = useState('');
  const [size, setSize] = useState('');
  const [bait, setBait] = useState('');
  const [rod, setRod] = useState('');
  const [image, setImage] = useState(null);
  const [location, setLocation] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [cameraSupported, setCameraSupported] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  // No group selection needed for catch entry

  useEffect(() => {
    if (typeof navigator === 'undefined') return;

    const hasCamera = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    if (!hasCamera) return;

    setCameraSupported(true);

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        streamRef.current = stream;
        setSuccess('Camera ready');
      } catch (err) {
        console.error('Error starting camera', err);
        setError('Unable to access camera. You can still upload a photo.');
      }
    };

    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleCapturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement('canvas');
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, width, height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `catch-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setImage(file);
        const previewUrl = URL.createObjectURL(blob);
        setPreview(previewUrl);
      },
      'image/jpeg',
      0.92
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      if (!user?.id) throw new Error('User not found');
      const formData = new FormData();
      formData.append('user_id', user.id);
      formData.append('species', species);
      formData.append('size', size);
      formData.append('bait', bait);
      formData.append('rod', rod);
      formData.append('location', location);
      if (image) formData.append('image', image);

      // Get JWT token from localStorage (if using auth)
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_API_URL + '/catches', {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit catch');
      }
      setSuccess('Catch submitted!');
      setSpecies('');
      setSize('');
      setBait('');
      setRod('');
      setLocation('');
      setImage(null);
      setPreview(null);
    } catch (err) {
      setError(err.message || 'Error submitting catch');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ mt: 4 }}>
      <Typography variant="h5" align="center" gutterBottom>Add Catches</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        {/* No group selection needed */}
        <TextField label="Species" fullWidth margin="normal" value={species} onChange={e => setSpecies(e.target.value)} required />
        <TextField label="Size (mm)" type="number" fullWidth margin="normal" value={size} onChange={e => setSize(e.target.value)} required />
        <TextField label="Bait" fullWidth margin="normal" value={bait} onChange={e => setBait(e.target.value)} />
        <TextField label="Rod" fullWidth margin="normal" value={rod} onChange={e => setRod(e.target.value)} />
        <TextField label="Location" fullWidth margin="normal" value={location} onChange={e => setLocation(e.target.value)} />
        {cameraSupported && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{ width: '100%', maxHeight: 300, borderRadius: 4 }}
            />
            <Button
              variant="contained"
              color="secondary"
              sx={{ mt: 1 }}
              onClick={handleCapturePhoto}
            >
              Take Photo
            </Button>
          </Box>
        )}
        <Button variant="contained" component="label" fullWidth sx={{ mt: 2 }}>
          Upload Photo
          <input
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={handleImageChange}
          />
        </Button>
        {preview && <Box sx={{ mt: 2, textAlign: 'center' }}><img src={preview} alt="preview" style={{ maxWidth: '100%', maxHeight: 200 }} /></Box>}
        <Button type="submit" fullWidth variant="contained" color="primary" disabled={loading} sx={{ mt: 2 }}>
          {loading ? 'Submitting...' : 'Submit Catch'}
        </Button>
      </Box>
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>
    </Container>
  );
}
